import { Grid, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { OverflowButton } from '$/components/common/OverflowButton';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { resendInvitation } from '$/services/usecases/invitation';
import { type StudentInvitationRequest } from '$/services/usecases/invitation/mapper/invitation';

interface Props {
  studentInvitation: StudentInvitationRequest;
  onEdit: (studentInvitation: StudentInvitationRequest) => void;
  onDelete: (studentInvitation: StudentInvitationRequest) => void;
}

export const InvitationCard: FC<Props> = ({
  studentInvitation,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const { mutate: resend, isPending } = useMutation({
    mutationFn: () => resendInvitation(studentInvitation.id),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.students.resendInvitationSuccess'), 'success', '');
    },
  });

  return (
    <Grid
      alignItems='center'
      templateColumns='minmax(0, 1fr) minmax(0, 0.5fr) minmax(0, 0.5fr) minmax(0, 1.5fr)'
      w='full'
      px='6'
      py='4'
      border='1px solid'
      borderColor='border'
      borderRadius='md'
    >
      <Stack gap='0'>
        <Text color='lighterText' fontSize='md'>
          {studentInvitation.email}
        </Text>
        <Text fontSize='md' fontWeight='bold'>
          {studentInvitation.firstName} {studentInvitation.lastName}
        </Text>
      </Stack>
      <Text>{studentInvitation.schoolClassName ?? '-'}</Text>
      <Text>
        {studentInvitation.expirationDate
          ? dayjs(studentInvitation.expirationDate).format('YYYY-MM-DD')
          : '-'}
      </Text>
      <HStack justifyContent='flex-end'>
        <OverflowButton
          px='6'
          fontSize='sm'
          isLoading={isPending}
          leftIcon={<Icon icon='refresh' w='14px' />}
          onClick={() => resend()}
          variant='tertiary'
        >
          {t('school.students.resendInvitationButton')}
        </OverflowButton>
        <OverflowButton
          px='6'
          fontSize='sm'
          leftIcon={<Icon icon='edit_pen' w='14px' />}
          onClick={() => onEdit(studentInvitation)}
          variant='tertiary'
          hideTextOnOverflow
        >
          {t('general.edit')}
        </OverflowButton>
        <OverflowButton
          px='6'
          fontSize='sm'
          leftIcon={<Icon icon='trash_can' w='14px' />}
          onClick={() => onDelete(studentInvitation)}
          variant='tertiary'
          hideTextOnOverflow
        >
          {t('general.delete')}
        </OverflowButton>
      </HStack>
    </Grid>
  );
};
